/**
 * Bound captured photos before they hit IndexedDB (doc §6): floor tablets
 * capture constantly and dead-zone queues must survive a whole shift, so
 * large images are downscaled/compressed on capture; non-image blobs and
 * environments without canvas pass through untouched.
 */
export async function compressImage(
  blob: Blob,
  options?: { maxDimension?: number; quality?: number },
): Promise<Blob> {
  const maxDimension = options?.maxDimension ?? 1600
  const quality = options?.quality ?? 0.8
  if (!blob.type.startsWith('image/') || blob.type === 'image/gif') return blob
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return blob
  try {
    const bitmap = await createImageBitmap(blob)
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height))
    if (scale === 1 && blob.size < 1_000_000) return blob
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    const ctx2d = canvas.getContext('2d')
    if (!ctx2d) return blob
    ctx2d.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality),
    )
    return out && out.size < blob.size ? out : blob
  } catch {
    return blob
  }
}
